import axios from 'axios'

// Dedicated client for the partner portal (partners.logeco.africa). It authenticates
// as a MobileUser (realm 'mobile') — the same account as the app — and is fully
// isolated from the admin axios instance.
const partner = axios.create({
  baseURL: process.env.VUE_APP_API_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
})

export const PARTNER_TOKEN_KEY = 'partnerToken'
export const PARTNER_ORG_KEY = 'partnerOrgId'

partner.interceptors.request.use((config) => {
  if (config.data instanceof FormData) delete config.headers['Content-Type']
  const token = localStorage.getItem(PARTNER_TOKEN_KEY)
  if (token) config.headers.Authorization = 'Bearer ' + token
  const orgId = localStorage.getItem(PARTNER_ORG_KEY)
  if (orgId) config.headers['X-Partner-Org-Id'] = orgId
  return config
})

partner.interceptors.response.use(
  (r) => r,
  (error) => {
    const url = error.config?.url || ''
    const isAuth = url.includes('/partner/auth/login')
    if (error.response?.status === 401 && !isAuth) {
      localStorage.removeItem(PARTNER_TOKEN_KEY)
      window.location.href = '/partner/login'
    }
    return Promise.reject(error)
  }
)

export function setPartnerToken(token) {
  if (token) localStorage.setItem(PARTNER_TOKEN_KEY, token)
  else localStorage.removeItem(PARTNER_TOKEN_KEY)
}

export function setPartnerOrg(orgId) {
  if (orgId) localStorage.setItem(PARTNER_ORG_KEY, orgId)
  else localStorage.removeItem(PARTNER_ORG_KEY)
}

export const partnerAuthAPI = {
  login: (email, password) => partner.post('/partner/auth/login', { email, password }),
  changePassword: (currentPassword, newPassword) =>
    partner.post('/partner/auth/change-password', { currentPassword, newPassword }),
}

export const partnerAPI = {
  me: () => partner.get('/partner/me'),
  getOrg: () => partner.get('/partner/org'),
  updateOrg: (data) => partner.put('/partner/org', data),
  uploadLogo: (file) => {
    const fd = new FormData()
    fd.append('file', file)
    return partner.post('/partner/org/logo', fd)
  },

  members: {
    list: () => partner.get('/partner/members'),
  },

  links: {
    list: (status) => partner.get('/partner/links', { params: { status } }),
    approve: (id) => partner.post('/partner/links/' + id + '/approve'),
    revoke: (id) => partner.post('/partner/links/' + id + '/revoke'),
  },

  beneficiaries: {
    list: () => partner.get('/partner/beneficiaries'),
    get: (id) => partner.get('/partner/beneficiaries/' + id),
  },

  needsAggregate: () => partner.get('/partner/needs/aggregate'),
  needsList: () => partner.get('/partner/needs'),

  forms: {
    list: () => partner.get('/partner/forms'),
    create: (data) => partner.post('/partner/forms', data),
    update: (id, data) => partner.put('/partner/forms/' + id, data),
    remove: (id) => partner.delete('/partner/forms/' + id),
    submissions: (id, periodKey) => partner.get('/partner/forms/' + id + '/submissions', { params: { periodKey } }),
  },

  documents: {
    list: (direction) => partner.get('/partner/documents', { params: { direction } }),
    upload: ({ file, title, note }) => {
      const fd = new FormData()
      fd.append('file', file)
      if (title) fd.append('title', title)
      if (note) fd.append('note', note)
      return partner.post('/partner/documents/upload', fd)
    },
    remove: (id) => partner.delete('/partner/documents/' + id),
  },

  checkins: () => partner.get('/partner/checkins'),

  events: {
    list: () => partner.get('/partner/events'),
    create: (data) => partner.post('/partner/events', data),
    update: (id, data) => partner.put('/partner/events/' + id, data),
    remove: (id) => partner.delete('/partner/events/' + id),
  },

  todos: {
    list: () => partner.get('/partner/todos'),
    create: (data) => partner.post('/partner/todos', data),
    update: (id, data) => partner.put('/partner/todos/' + id, data),
    remove: (id) => partner.delete('/partner/todos/' + id),
    completions: (id) => partner.get('/partner/todos/' + id + '/completions'),
  },

  broadcast: (title, body) => partner.post('/partner/notifications', { title, body }),
  messages: () => partner.get('/partner/messages'),
  exportReport: () => partner.get('/partner/exports/report', { responseType: 'blob' }),
  audit: () => partner.get('/partner/audit'),
}

export default partner
